import { useState, type CSSProperties } from 'react';
import type { LucideIcon } from 'lucide-react';
import type { Theme } from '../types/theme';
import { localizedPageContent } from '../data/pageContent';
import type { Locale } from '../i18n';
import { getAccessibleAccentColor, getAccessibleTextColor, isDarkColor, withHexAlpha } from '../utils/color';
import {
  Zap,
  Brain,
  Shield,
  Layers,
  GitBranch,
  Users,
  ChevronRight,
  MessageSquare,
  BarChart3,
  Code2,
  Workflow,
} from 'lucide-react';

interface ProductLandingPageProps {
  theme: Theme;
  locale?: Locale;
  showHeader?: boolean;
  compact?: boolean;
  isMobile?: boolean;
}

type LandingSection = 'features' | 'demo' | 'pricing' | 'resources';

const iconMap: Record<string, LucideIcon> = {
  zap: Zap,
  brain: Brain,
  shield: Shield,
  layers: Layers,
  'git-branch': GitBranch,
  users: Users,
};

const mockupIconMap: Record<string, LucideIcon> = {
  chat: MessageSquare,
  dashboard: BarChart3,
  code: Code2,
  workflow: Workflow,
};

export function ProductLandingPage({ theme, locale = 'en', showHeader = true, compact = false, isMobile = false }: ProductLandingPageProps) {
  const {
    colors,
    typography,
    radius,
    shadow,
    layout,
  } = theme;
  const content = localizedPageContent[locale];
  const defaultPlanIndex = Math.max(content.pricing.plans.findIndex((plan) => plan.popular), 0);
  const [selectedPlanIndex, setSelectedPlanIndex] = useState(defaultPlanIndex);

  const isDark = isDarkColor(colors.background);
  const pageTextPrimary = getAccessibleTextColor(colors.textPrimary, colors.background);
  const pageTextSecondary = getAccessibleTextColor(colors.textSecondary, colors.background);
  const surfaceTextPrimary = getAccessibleTextColor(colors.textPrimary, colors.surface);
  const surfaceTextMuted = getAccessibleTextColor(colors.textMuted, colors.surface, 3);
  const surfaceAccent = getAccessibleAccentColor(colors.primary, colors.surface, colors.primaryHover);
  const backgroundAccent = getAccessibleAccentColor(colors.primary, colors.background, colors.primaryHover);
  const primaryButtonText = getAccessibleTextColor(colors.onPrimary, colors.primary);
  const elevatedBackground = colors.surfaceElevated || colors.surface;
  const elevatedText = getAccessibleTextColor(colors.textPrimary, elevatedBackground);
  const darkPanelBackground = isDark ? colors.surfaceDark || colors.surface : '#1f2937';
  const safeHeadingStyle: CSSProperties = {
    fontFamily: typography.headingFont,
    fontWeight: typography.headingWeight,
    letterSpacing: '0',
  };
  const mobileHeadingStyle: CSSProperties = {
    ...safeHeadingStyle,
    fontSize: '44px',
    lineHeight: 1.04,
    overflowWrap: 'anywhere',
    wordBreak: 'normal',
  };
  const sectionPaddingClass = isMobile ? 'px-4 py-10' : 'px-4 py-14 sm:px-6 md:py-20';
  const featureGridClass = isMobile ? 'grid gap-4' : 'grid gap-6 md:grid-cols-2 lg:grid-cols-3';
  const pricingGridClass = isMobile ? 'grid gap-4' : 'grid gap-6 md:grid-cols-3';
  const navTargets: LandingSection[] = ['features', 'demo', 'pricing', 'resources', 'resources'];
  const resourceItems = content.footer.links[1]?.items || [];

  const scrollToSection = (section: LandingSection) => {
    const target = document.getElementById(`landing-${section}`);
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handlePlanSelect = (planIndex = selectedPlanIndex) => {
    setSelectedPlanIndex(planIndex);
    scrollToSection('pricing');
  };

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundColor: colors.background,
        fontFamily: typography.bodyFont,
      }}
    >
      {showHeader && (
        <header
          className="sticky top-0 z-50 border-b"
          style={{
            backgroundColor: colors.background,
            borderColor: colors.border,
            minHeight: layout.navHeight,
          }}
        >
          <div
            className="mx-auto flex min-h-16 items-center justify-between gap-3 px-4 sm:px-6"
            style={{ maxWidth: layout.maxWidth }}
          >
            <div className="flex min-w-0 items-center gap-6">
              <span className="whitespace-nowrap text-lg font-semibold" style={{ color: pageTextPrimary, letterSpacing: '0' }}>
                {content.nav.logo}
              </span>
              {!isMobile && (
                <nav className="hidden items-center gap-2 md:flex" aria-label="Preview navigation">
                  {content.nav.menuItems.map((item, index) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => scrollToSection(navTargets[index] || 'features')}
                      className="min-h-11 rounded-lg px-3 text-sm transition-opacity hover:opacity-75"
                      style={{ color: pageTextSecondary }}
                    >
                      {item}
                    </button>
                  ))}
                </nav>
              )}
            </div>
            {!isMobile && (
              <button
                type="button"
                onClick={() => handlePlanSelect(defaultPlanIndex)}
                className="min-h-11 flex-shrink-0 px-4 text-sm font-medium transition-opacity hover:opacity-90"
                style={{
                  backgroundColor: colors.primary,
                  color: primaryButtonText,
                  borderRadius: radius.button,
                  ...(shadow.button && { boxShadow: shadow.button }),
                }}
              >
                {content.nav.ctaText}
              </button>
            )}
          </div>
        </header>
      )}

      <main>
        <section className={sectionPaddingClass} style={{ paddingTop: compact ? '48px' : undefined }}>
          <div
            className={isMobile ? 'mx-auto grid gap-8' : 'mx-auto grid items-center gap-12 lg:grid-cols-2 lg:gap-16'}
            style={{ maxWidth: layout.maxWidth }}
          >
            <div className={isMobile ? 'space-y-5' : 'space-y-6'}>
              <span
                className="inline-block rounded-full px-3 py-1 text-xs font-medium"
                style={{
                  backgroundColor: colors.surface,
                  color: surfaceAccent,
                  borderRadius: radius.badge,
                }}
              >
                {content.hero.badge}
              </span>
              <h1
                className={isMobile ? '' : 'text-4xl leading-tight md:text-5xl lg:text-6xl'}
                style={isMobile ? { ...mobileHeadingStyle, color: pageTextPrimary } : { ...safeHeadingStyle, color: pageTextPrimary, lineHeight: 1.1 }}
              >
                {content.hero.title}
              </h1>
              <p
                className={isMobile ? 'text-base' : 'max-w-xl text-lg md:text-xl'}
                style={{ color: pageTextSecondary, lineHeight: 1.6 }}
              >
                {content.hero.subtitle}
              </p>
              <div className={isMobile ? 'grid gap-3 pt-1' : 'flex flex-wrap gap-4 pt-2'}>
                <button
                  type="button"
                  onClick={() => handlePlanSelect(defaultPlanIndex)}
                  className="min-h-11 px-6 text-base font-medium transition-opacity hover:opacity-90"
                  style={{
                    backgroundColor: colors.primary,
                    color: primaryButtonText,
                    borderRadius: radius.button,
                  }}
                >
                  {content.hero.ctaPrimary}
                </button>
                <button
                  type="button"
                  onClick={() => scrollToSection('demo')}
                  className="min-h-11 px-6 text-base font-medium transition-opacity hover:opacity-80"
                  style={{
                    backgroundColor: 'transparent',
                    color: pageTextPrimary,
                    borderRadius: radius.button,
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  {content.hero.ctaSecondary}
                  <ChevronRight className="ml-1 inline-block" size={16} aria-hidden="true" />
                </button>
              </div>
            </div>

            <div
              className={isMobile ? 'relative overflow-hidden p-4' : 'relative p-6'}
              style={{
                backgroundColor: colors.surface,
                borderRadius: radius.card,
                boxShadow: shadow.card,
                border: `1px solid ${colors.border}`,
              }}
            >
              <div className="space-y-4">
                <div className="flex items-center gap-3" aria-hidden="true">
                  <div className="h-3 w-3 rounded-full" style={{ backgroundColor: '#dc2626' }} />
                  <div className="h-3 w-3 rounded-full" style={{ backgroundColor: '#ca8a04' }} />
                  <div className="h-3 w-3 rounded-full" style={{ backgroundColor: '#16a34a' }} />
                </div>
                <div className="space-y-2 font-mono text-sm">
                  <div style={{ color: surfaceTextMuted }}>
                    <span style={{ color: surfaceAccent }}>const</span>{' '}
                    <span style={{ color: surfaceTextPrimary }}>ai</span>{' '}
                    <span style={{ color: surfaceTextMuted }}>=</span>{' '}
                    <span style={{ color: surfaceAccent }}>new</span>{' '}
                    <span style={{ color: surfaceTextPrimary }}>NeuroAI</span>
                    <span style={{ color: surfaceTextMuted }}>();</span>
                  </div>
                  <div style={{ color: surfaceTextMuted }}>
                    <span style={{ color: surfaceAccent }}>await</span>{' '}
                    <span style={{ color: surfaceTextPrimary }}>ai</span>
                    <span style={{ color: surfaceTextMuted }}>.</span>
                    <span style={{ color: surfaceTextPrimary }}>complete</span>
                    <span style={{ color: surfaceTextMuted }}>(</span>
                    <span style={{ color: getAccessibleAccentColor(colors.accent, colors.surface, surfaceAccent) }}>'Build a REST API'</span>
                    <span style={{ color: surfaceTextMuted }}>);</span>
                  </div>
                  <div
                    className="mt-4 rounded-lg p-4"
                    style={{ backgroundColor: elevatedBackground, border: `1px solid ${colors.border}` }}
                  >
                    <span style={{ color: getAccessibleAccentColor(colors.primary, elevatedBackground, colors.primaryHover) }}>-&gt;</span>{' '}
                        <span style={{ color: getAccessibleTextColor(colors.textSecondary, elevatedBackground) }}>
                      {content.demo.completion}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          id="landing-features"
          className={sectionPaddingClass}
          style={{
            backgroundColor: colors.surface,
            paddingTop: compact ? '32px' : undefined,
            paddingBottom: compact ? '32px' : undefined,
          }}
        >
          <div className="mx-auto space-y-12" style={{ maxWidth: layout.maxWidth }}>
            <div className="space-y-4 text-center">
              <span
                className="inline-block rounded-full px-3 py-1 text-xs font-medium"
                style={{ backgroundColor: colors.background, color: backgroundAccent, borderRadius: radius.badge }}
              >
                {content.features.badge}
              </span>
              <h2 className="text-3xl md:text-4xl" style={{ ...safeHeadingStyle, color: surfaceTextPrimary }}>
                {content.features.title}
              </h2>
            </div>

            <div className={featureGridClass}>
              {content.features.items.map((feature, index) => {
                const IconComponent = iconMap[feature.icon];
                return (
                  <div
                    key={index}
                    className="p-6 transition-shadow hover:shadow-md"
                    style={{
                      backgroundColor: colors.background,
                      borderRadius: radius.card,
                      boxShadow: shadow.card,
                      border: `1px solid ${colors.border}`,
                    }}
                  >
                    <div
                      className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl"
                      style={{
                        backgroundColor: isDark ? elevatedBackground : withHexAlpha(colors.primary, '1f'),
                        color: getAccessibleAccentColor(colors.primary, isDark ? elevatedBackground : colors.background, colors.primaryHover),
                      }}
                      aria-hidden="true"
                    >
                      {IconComponent && <IconComponent size={24} />}
                    </div>
                    <h3 className="mb-2 text-lg font-semibold" style={{ color: pageTextPrimary }}>
                      {feature.title}
                    </h3>
                    <p className="text-sm" style={{ color: pageTextSecondary, lineHeight: 1.6 }}>
                      {feature.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section
          id="landing-demo"
          className={sectionPaddingClass}
          style={{ paddingTop: compact ? '32px' : undefined, paddingBottom: compact ? '32px' : undefined }}
        >
          <div className="mx-auto space-y-8" style={{ maxWidth: layout.maxWidth }}>
            <div className="space-y-4 text-center">
              <span
                className="inline-block rounded-full px-3 py-1 text-xs font-medium"
                style={{ backgroundColor: colors.surface, color: surfaceAccent, borderRadius: radius.badge }}
              >
                {content.demo.badge}
              </span>
              <h2 className="text-3xl md:text-4xl" style={{ ...safeHeadingStyle, color: pageTextPrimary }}>
                {content.demo.title}
              </h2>
              <p className="mx-auto max-w-2xl text-lg" style={{ color: pageTextSecondary }}>
                {content.demo.description}
              </p>
            </div>

            <div
              className="overflow-hidden"
              style={{
                backgroundColor: darkPanelBackground,
                borderRadius: radius.card,
                boxShadow: shadow.elevated,
                border: `1px solid ${colors.border}`,
              }}
            >
              <div className={isMobile ? 'p-4' : 'p-6 md:p-12'}>
                <div className="mx-auto max-w-3xl space-y-6">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div
                      className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full"
                      style={{ backgroundColor: colors.primary, color: primaryButtonText }}
                      aria-hidden="true"
                    >
                      {(() => {
                        const Icon = mockupIconMap[content.demo.mockupType];
                        return Icon && <Icon size={20} />;
                      })()}
                    </div>
                    <div className="flex-1 space-y-3">
                      <div
                        className="rounded-2xl rounded-tl-sm p-4"
                        style={{
                          backgroundColor: isDark ? elevatedBackground : '#f1f5f9',
                          color: isDark ? elevatedText : '#111827',
                        }}
                      >
                        <p className="text-sm md:text-base">
                          {content.demo.prompt}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start justify-end gap-3 sm:gap-4">
                    <div className="flex flex-1 justify-end">
                      <div
                        className="max-w-lg rounded-2xl rounded-tr-sm p-4"
                        style={{ backgroundColor: colors.primary, color: primaryButtonText }}
                      >
                        <p className="text-sm md:text-base">
                          {content.demo.response}
                        </p>
                      </div>
                    </div>
                    <div
                      className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full"
                      style={{
                        backgroundColor: elevatedBackground,
                        color: getAccessibleAccentColor(colors.primary, elevatedBackground, colors.primaryHover),
                      }}
                      aria-hidden="true"
                    >
                      <Code2 size={20} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          id="landing-pricing"
          className={sectionPaddingClass}
          style={{
            backgroundColor: colors.surface,
            paddingTop: compact ? '32px' : undefined,
            paddingBottom: compact ? '32px' : undefined,
          }}
        >
          <div className="mx-auto space-y-12" style={{ maxWidth: layout.maxWidth }}>
            <div className="space-y-4 text-center">
              <span
                className="inline-block rounded-full px-3 py-1 text-xs font-medium"
                style={{ backgroundColor: colors.background, color: backgroundAccent, borderRadius: radius.badge }}
              >
                {content.pricing.badge}
              </span>
              <h2 className="text-3xl md:text-4xl" style={{ ...safeHeadingStyle, color: surfaceTextPrimary }}>
                {content.pricing.title}
              </h2>
            </div>

            <div className={pricingGridClass}>
              {content.pricing.plans.map((plan, index) => {
                const isSelected = selectedPlanIndex === index;
                const planBackground = plan.popular
                  ? (isDark ? colors.surfaceDark || colors.surface : colors.background)
                  : colors.background;
                const planText = getAccessibleTextColor(colors.textPrimary, planBackground);
                const planSecondary = getAccessibleTextColor(colors.textSecondary, planBackground);
                const planMuted = getAccessibleTextColor(colors.textMuted, planBackground, 3);
                const planAccent = getAccessibleAccentColor(colors.primary, planBackground, colors.primaryHover);
                const successText = getAccessibleAccentColor(colors.success || colors.primary, planBackground, colors.primaryHover, 3);

                return (
                  <div
                    key={index}
                    className="relative flex flex-col p-6 transition-shadow hover:shadow-md"
                    style={{
                      backgroundColor: planBackground,
                      borderRadius: radius.card,
                      boxShadow: isSelected || plan.popular ? shadow.elevated : shadow.card,
                      border: `1px solid ${isSelected || plan.popular ? planAccent : colors.border}`,
                    }}
                  >
                    {plan.popular && (
                      <span
                        className="absolute -top-3 left-6 rounded-full px-3 py-1 text-xs font-medium"
                        style={{
                          backgroundColor: colors.primary,
                          color: primaryButtonText,
                          borderRadius: radius.badge,
                        }}
                      >
                        {content.pricing.popularLabel}
                      </span>
                    )}
                    <div className="flex-1 space-y-4">
                      <h3 className="text-xl font-semibold" style={{ color: planText }}>
                        {plan.name}
                      </h3>
                      <div className="flex items-baseline gap-2">
                        <span className="text-4xl font-bold" style={{ color: planText }}>
                          {plan.price}
                        </span>
                        <span className="text-sm" style={{ color: planMuted }}>
                          / {plan.period}
                        </span>
                      </div>
                      <p className="text-sm" style={{ color: planSecondary }}>
                        {plan.description}
                      </p>
                      <ul className="space-y-2 pt-2">
                        {plan.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-center gap-2 text-sm" style={{ color: planSecondary }}>
                            <span style={{ color: successText }} aria-hidden="true">✓</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedPlanIndex(index)}
                      aria-pressed={isSelected}
                      className="mt-6 min-h-11 w-full px-6 text-base font-medium transition-opacity hover:opacity-90"
                      style={{
                        backgroundColor: isSelected || plan.popular ? colors.primary : 'transparent',
                        color: isSelected || plan.popular ? primaryButtonText : planAccent,
                        borderRadius: radius.button,
                        border: isSelected || plan.popular ? 'none' : `1px solid ${planAccent}`,
                      }}
                    >
                      {plan.cta}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section
          id="landing-resources"
          className={isMobile ? 'px-4 py-10' : 'px-4 py-14 sm:px-6'}
          style={{ backgroundColor: colors.background }}
        >
          <div
            className={isMobile ? 'mx-auto grid gap-4' : 'mx-auto grid items-center gap-8 md:grid-cols-[0.85fr_1.15fr]'}
            style={{ maxWidth: layout.maxWidth }}
          >
            <div>
              <span
                className="inline-block rounded-full px-3 py-1 text-xs font-medium"
                style={{ backgroundColor: colors.surface, color: surfaceAccent, borderRadius: radius.badge }}
              >
                {content.footer.links[1]?.title || 'Resources'}
              </span>
              <h2 className="mt-3 text-2xl md:text-3xl" style={{ ...safeHeadingStyle, color: pageTextPrimary }}>
                {locale === 'zh' ? '继续了解产品能力' : 'Continue exploring the product'}
              </h2>
              <p className="mt-3 text-sm md:text-base" style={{ color: pageTextSecondary, lineHeight: 1.65 }}>
                {locale === 'zh'
                  ? '文档、API 参考、博客和社区作为资源入口保留在落地页结构中，用来检验导航与内容层级。'
                  : 'Docs, API references, blog posts, and community links stay inside the landing structure to test navigation and hierarchy.'}
              </p>
            </div>
            <div className={isMobile ? 'grid gap-3' : 'grid gap-3 sm:grid-cols-2'}>
              {resourceItems.map((item, index) => (
                <button
                  key={item}
                  type="button"
                  className="flex min-h-14 items-center justify-between rounded-lg border px-4 text-left text-sm font-medium transition-opacity hover:opacity-80"
                  style={{
                    backgroundColor: colors.surface,
                    borderColor: colors.border,
                    color: surfaceTextPrimary,
                    borderRadius: radius.card,
                  }}
                >
                  {item}
                  <span
                    className="flex h-7 w-7 items-center justify-center rounded-full"
                    style={{ backgroundColor: withHexAlpha(colors.primary, index === 0 ? '24' : '16'), color: surfaceAccent }}
                    aria-hidden="true"
                  >
                    <ChevronRight size={15} />
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer
        className={isMobile ? 'border-t px-4 py-10' : 'border-t px-4 py-12 sm:px-6 md:py-16'}
        style={{
          backgroundColor: isDark ? colors.surfaceDark || colors.surface : colors.surface,
          borderColor: colors.border,
        }}
      >
        <div className="mx-auto" style={{ maxWidth: layout.maxWidth }}>
          <div className={isMobile ? 'grid gap-7' : 'grid gap-8 md:grid-cols-5'}>
            <div className={isMobile ? '' : 'md:col-span-1'}>
              <span className="text-lg font-semibold" style={{ color: surfaceTextPrimary }}>
                {content.footer.brand}
              </span>
              <p
                className={isMobile ? 'mt-2 max-w-[24ch] text-sm' : 'mt-2 text-sm'}
                style={{ color: surfaceTextMuted, lineHeight: 1.55 }}
              >
                {content.footer.tagline}
              </p>
            </div>
            <div className={isMobile ? 'grid grid-cols-2 gap-x-5 gap-y-7' : 'grid grid-cols-2 gap-8 md:col-span-4 md:grid-cols-4'}>
              {content.footer.links.map((linkGroup, index) => (
                <div key={index}>
                  <h4 className="mb-3 text-sm font-semibold" style={{ color: surfaceTextPrimary }}>
                    {linkGroup.title}
                  </h4>
                  <ul className="space-y-2.5">
                    {linkGroup.items.map((item, itemIndex) => (
                      <li key={itemIndex}>
                        <span className="block text-sm" style={{ color: surfaceTextMuted, lineHeight: 1.35 }}>
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
          <div
            className="mt-12 border-t pt-8 text-center text-sm"
            style={{ borderColor: colors.border, color: surfaceTextMuted }}
          >
            © 2024 {content.footer.brand}. {content.footer.copyright}
          </div>
        </div>
      </footer>
    </div>
  );
}
